import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MdChevronLeft, MdChevronRight, MdZoomIn, MdZoomOut, MdClose, MdOpenInFull, MdDownload, MdRefresh } from 'react-icons/md';
import { THEME } from '../theme';
import ImageGenerationLoader from '../../common/ImageGenerationLoader';
import TearSheetSlide from './TearSheetSlide';
import type { ProductConfig } from '../customizerConfig';

interface GalleryImage {
  url: string;
  label?: string;
}

interface ImageGalleryProps {
  images: GalleryImage[];
  isLoading?: boolean;
  loadingCount?: number;
  activeIndex?: number;
  onIndexChange?: (index: number) => void;
  onRegenerate?: (index: number) => void;
  isRegenerating?: boolean;
  canRegenerate?: (index: number) => boolean;
  product?: ProductConfig;
}

const ImageGallery: React.FC<ImageGalleryProps> = ({ images, isLoading = false, loadingCount = 0, activeIndex, onIndexChange, onRegenerate, isRegenerating = false, canRegenerate, product }) => {
  const [internalIndex, setInternalIndex] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);

  const isControlled = activeIndex !== undefined;
  const selectedIndex = isControlled ? activeIndex : internalIndex;

  const updateIndex = useCallback((i: number) => {
    if (isControlled) onIndexChange?.(i);
    else setInternalIndex(i);
  }, [isControlled, onIndexChange]);

  useEffect(() => {
    if (images.length > 0 && selectedIndex >= images.length) updateIndex(images.length - 1);
  }, [images.length, selectedIndex, updateIndex]);

  const currentImage = images[selectedIndex]?.url || null;
  const currentLabel = images[selectedIndex]?.label || '';
  const isTearSheetSlide = product && currentLabel.toLowerCase().includes('tear sheet');
  const remainingSkeletons = isLoading ? Math.max(0, loadingCount - images.length) : 0;
  const hasImages = images.length > 0;

  const goNext = useCallback(() => {
    if (selectedIndex < images.length - 1) { updateIndex(selectedIndex + 1); setZoomLevel(1); }
  }, [selectedIndex, images.length, updateIndex]);

  const goPrev = useCallback(() => {
    if (selectedIndex > 0) { updateIndex(selectedIndex - 1); setZoomLevel(1); }
  }, [selectedIndex, updateIndex]);

  const handleDownload = async () => {
    if (!currentImage) return;
    try {
      const res = await fetch(currentImage);
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${images[selectedIndex]?.label || `view-${selectedIndex + 1}`}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch { window.open(currentImage, '_blank'); }
  };

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'ArrowLeft') goPrev();
      if (e.key === 'Escape') { setIsFullscreen(false); setZoomLevel(1); }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [goNext, goPrev]);

  return (
    <>
      <div className="flex h-full gap-4">
        <div className="w-20 flex-shrink-0 flex flex-col gap-2.5 overflow-y-auto py-1 pr-1">
          {images.map((img, idx) => {
            const isActive = selectedIndex === idx;
            return (
              <motion.button
                key={`thumb-${idx}`}
                initial={{ opacity: 0, x: -12 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: idx * 0.05, duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
                onClick={() => { updateIndex(idx); setZoomLevel(1); }}
                className="relative w-[76px] h-[64px] flex-shrink-0 rounded-xl overflow-hidden cursor-pointer transition-all duration-300"
                style={{
                  opacity: isActive ? 1 : 0.35,
                  transform: isActive ? 'scale(1)' : 'scale(0.95)',
                  boxShadow: isActive ? `0 0 0 2px ${THEME.primary}, ${THEME.primaryGlow}` : 'none',
                  border: isActive ? 'none' : '1px solid rgba(255,255,255,0.06)',
                }}
                whileHover={{ opacity: isActive ? 1 : 0.7, scale: 1 }}
              >
                <img
                  src={img.url}
                  alt={img.label || `View ${idx + 1}`}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                {isActive && (
                  <motion.div
                    layoutId="thumbIndicator"
                    className="absolute left-0 top-0 bottom-0 w-[3px] rounded-r-full"
                    style={{ background: THEME.primary, boxShadow: `0 0 8px ${THEME.primary}` }}
                    transition={{ type: 'spring', stiffness: 500, damping: 35 }}
                  />
                )}
              </motion.button>
            );
          })}

          {Array.from({ length: remainingSkeletons }, (_, i) => (
            <div
              key={`skel-${i}`}
              className="w-[76px] h-[64px] flex-shrink-0 rounded-xl relative overflow-hidden"
              style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)' }}
            >
              <div className="absolute inset-0 animate-pulse bg-gradient-to-r from-transparent via-white/[0.04] to-transparent" />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-5 h-5 rounded-full border-2 border-gray-700 animate-spin" style={{ borderTopColor: THEME.primary }} />
              </div>
            </div>
          ))}
        </div>

        <div className="flex-1 relative rounded-2xl overflow-hidden group/hero" style={{ background: isTearSheetSlide ? 'transparent' : 'linear-gradient(145deg, #ffffff, #f8f7f5, #f3f1ee)' }}>
          <AnimatePresence mode="wait">
            {isTearSheetSlide && currentImage ? (
              <motion.div
                key={`tearsheet-${currentImage}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.4 }}
                className="w-full h-full"
              >
                <TearSheetSlide product={product} imageUrl={currentImage} />
              </motion.div>
            ) : currentImage ? (
              <motion.div
                key={currentImage}
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
                className="w-full h-full flex items-center justify-center overflow-hidden"
              >
                <img
                  src={currentImage}
                  alt="Product view"
                  className="w-full h-full object-contain transition-transform duration-300 ease-out"
                  style={{ transform: `scale(${zoomLevel})` }}
                  draggable={false}
                />
              </motion.div>
            ) : (
              <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="w-full h-full flex items-center justify-center">
                {!isLoading && (
                  <p className="text-sm text-gray-400">Select a product to begin</p>
                )}
              </motion.div>
            )}
          </AnimatePresence>

          {images.length > 1 && (
            <>
              {selectedIndex > 0 && (
                <button
                  onClick={goPrev}
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-black/30 backdrop-blur-md flex items-center justify-center cursor-pointer hover:bg-black/50 transition-all duration-300 opacity-0 group-hover/hero:opacity-100 z-10 border border-white/10"
                >
                  <MdChevronLeft size={22} className="text-white" />
                </button>
              )}
              {selectedIndex < images.length - 1 && (
                <button
                  onClick={goNext}
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-black/30 backdrop-blur-md flex items-center justify-center cursor-pointer hover:bg-black/50 transition-all duration-300 opacity-0 group-hover/hero:opacity-100 z-10 border border-white/10"
                >
                  <MdChevronRight size={22} className="text-white" />
                </button>
              )}
            </>
          )}

          {currentImage && !isTearSheetSlide && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-1.5 px-3 py-2 rounded-full bg-black/40 backdrop-blur-xl opacity-0 group-hover/hero:opacity-100 transition-all duration-300 z-10 border border-white/10">
              <button onClick={() => setZoomLevel((z) => Math.max(z - 0.3, 1))} disabled={zoomLevel <= 1} className="w-8 h-8 rounded-full flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 transition-all cursor-pointer disabled:opacity-30">
                <MdZoomOut size={16} />
              </button>
              <span className="text-[10px] text-white/50 font-medium tabular-nums w-10 text-center">{Math.round(zoomLevel * 100)}%</span>
              <button onClick={() => setZoomLevel((z) => Math.min(z + 0.3, 3))} disabled={zoomLevel >= 3} className="w-8 h-8 rounded-full flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 transition-all cursor-pointer disabled:opacity-30">
                <MdZoomIn size={16} />
              </button>
              <div className="w-px h-5 bg-white/15 mx-1" />
              <button onClick={handleDownload} className="w-8 h-8 rounded-full flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 transition-all cursor-pointer">
                <MdDownload size={16} />
              </button>
              {onRegenerate && canRegenerate?.(selectedIndex) && (
                <button
                  onClick={() => onRegenerate(selectedIndex)}
                  disabled={isRegenerating || isLoading}
                  className="w-8 h-8 rounded-full flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 transition-all cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                  title="Regenerate this image"
                >
                  {isRegenerating ? (
                    <div className="w-3.5 h-3.5 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                  ) : (
                    <MdRefresh size={16} />
                  )}
                </button>
              )}
              <button onClick={() => setIsFullscreen(true)} className="w-8 h-8 rounded-full flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 transition-all cursor-pointer">
                <MdOpenInFull size={14} />
              </button>
              <div className="w-px h-5 bg-white/15 mx-1" />
              <span className="text-[10px] text-white/50 font-medium tabular-nums px-1">{selectedIndex + 1} / {isLoading ? `${images.length}+` : images.length}</span>
            </div>
          )}

          {currentImage && isTearSheetSlide && (
            <div className="absolute bottom-[21%] left-1/2 -translate-x-1/2 flex items-center gap-1.5 px-3 py-2 rounded-full bg-black/40 backdrop-blur-xl opacity-0 group-hover/hero:opacity-100 transition-all duration-300 z-10 border border-white/10">
              <button onClick={handleDownload} className="w-8 h-8 rounded-full flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 transition-all cursor-pointer">
                <MdDownload size={16} />
              </button>
              <button onClick={() => setIsFullscreen(true)} className="w-8 h-8 rounded-full flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 transition-all cursor-pointer">
                <MdOpenInFull size={14} />
              </button>
              <div className="w-px h-5 bg-white/15 mx-1" />
              <span className="text-[10px] text-white/50 font-medium tabular-nums px-1">{selectedIndex + 1} / {isLoading ? `${images.length}+` : images.length}</span>
            </div>
          )}

          <AnimatePresence>
            {isLoading && !hasImages && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 z-20">
                <ImageGenerationLoader />
              </motion.div>
            )}
          </AnimatePresence>

          <AnimatePresence>
            {isLoading && hasImages && (
              <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="absolute top-0 left-0 right-0 z-20">
                <div className="h-[3px] bg-black/5 w-full overflow-hidden rounded-full">
                  <motion.div
                    className="h-full rounded-full"
                    style={{ background: `linear-gradient(90deg, ${THEME.primaryDark}, ${THEME.primary}, ${THEME.primaryLight})` }}
                    initial={{ width: '0%' }}
                    animate={{ width: `${Math.max(5, (images.length / Math.max(loadingCount, images.length)) * 100)}%` }}
                    transition={{ duration: 0.6, ease: 'easeOut' }}
                  />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      <AnimatePresence>
        {isFullscreen && currentImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/95 backdrop-blur-2xl flex items-center justify-center"
            onClick={() => { setIsFullscreen(false); setZoomLevel(1); }}
          >
            <button onClick={() => { setIsFullscreen(false); setZoomLevel(1); }} className="absolute top-6 right-6 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center cursor-pointer z-20 border border-white/10 transition-all">
              <MdClose size={22} className="text-white" />
            </button>
            {selectedIndex > 0 && (
              <button onClick={(e) => { e.stopPropagation(); goPrev(); }} className="absolute left-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center cursor-pointer z-20 border border-white/10 transition-all">
                <MdChevronLeft size={28} className="text-white" />
              </button>
            )}
            {selectedIndex < images.length - 1 && (
              <button onClick={(e) => { e.stopPropagation(); goNext(); }} className="absolute right-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center cursor-pointer z-20 border border-white/10 transition-all">
                <MdChevronRight size={28} className="text-white" />
              </button>
            )}
            <motion.img
              key={currentImage}
              initial={{ scale: 0.88, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.88, opacity: 0 }}
              transition={{ type: 'spring', damping: 28, stiffness: 280 }}
              src={currentImage}
              alt="Fullscreen"
              className="max-w-[92vw] max-h-[92vh] object-contain rounded-2xl"
              onClick={(e) => e.stopPropagation()}
              draggable={false}
            />
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 px-5 py-2.5 rounded-full bg-white/10 backdrop-blur-md border border-white/10">
              <span className="text-xs text-white/80 font-medium tracking-wide">{selectedIndex + 1} of {images.length}</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ImageGallery;
